import { ReactElement, useMemo } from 'react';
import { useLocation as useDashboardLocation, useRouter } from '@tanstack/react-router';
import { createPath, NavigationType, Route, Router, Routes, type Navigator, type To } from 'react-router-dom';

type ReactRouterRouteAdapterProps = {
  children: ReactElement;
  path: string;
};

const toPath = (to: To): string => (typeof to === 'string' ? to : createPath(to));

export default function ReactRouterRouteAdapter({ children, path }: ReactRouterRouteAdapterProps) {
  const dashboardRouter = useRouter();
  const dashboardLocation = useDashboardLocation();

  const navigator = useMemo<Navigator>(
    () => ({
      createHref: (to) => dashboardRouter.history.createHref(toPath(to)),
      go: (delta) => dashboardRouter.history.go(delta),
      push: (to, state) => dashboardRouter.history.push(toPath(to), state),
      replace: (to, state) => dashboardRouter.history.replace(toPath(to), state)
    }),
    [dashboardRouter]
  );

  const location = useMemo(
    () => ({
      hash: dashboardLocation.hash,
      key: dashboardLocation.state.__TSR_key ?? dashboardLocation.state.key ?? 'default',
      pathname: dashboardLocation.pathname,
      search: dashboardLocation.searchStr,
      state: dashboardLocation.state
    }),
    [dashboardLocation]
  );

  return (
    <Router location={location} navigationType={NavigationType.Push} navigator={navigator}>
      <Routes>
        <Route path={path} element={children} />
      </Routes>
    </Router>
  );
}
